import { useState, useEffect, useCallback } from 'react';

const DRAFT_KEY = 'vip_draft_post';

/**
 * Custom hook to cache draft post data in localStorage
 * Saves: text content, post type, price, poll options, categories, and uploaded media metadata
 * Note: Only media that has been uploaded to S3 can be restored (has s3Key)
 */
export const useDraftPost = (isEditing = false) => {
  const [hasDraft, setHasDraft] = useState(false);

  // Check if there's a saved draft on mount
  useEffect(() => {
    if (!isEditing) {
      const draft = localStorage.getItem(DRAFT_KEY);
      setHasDraft(!!draft);
    }
  }, [isEditing]);

  // Save draft to localStorage (immediate, no debounce)
  const saveDraft = useCallback((draftData) => {
    if (isEditing) return; // Don't cache when editing existing posts

    try {
      console.log('[Draft] Raw media items to save:', draftData.mediaItems);
      
      // Only save media items that have been uploaded to S3
      const uploadedMedia = (draftData.mediaItems || [])
        .filter(item => item.s3Key && !item.uploading && !item.error)
        .map(item => ({
          id: item.id,
          s3Key: item.s3Key,
          thumbnailS3Key: item.thumbnailS3Key,
          thumbnailUrl: item.thumbnailUrl,
          url: item.url || item.fileUrl,
          fileUrl: item.fileUrl || item.url,
          mediaType: item.mediaType,
          fileName: item.fileName || item.file?.name,
          isFreePreview: item.isFreePreview,
          thumbnails: item.thumbnails,
          selectedThumbnailIndex: item.selectedThumbnailIndex,
          width: item.width,
          height: item.height,
          duration: item.duration
        }));
      
      console.log('[Draft] Filtered uploaded media to save:', uploadedMedia);

      const draft = {
        content: draftData.content || '',
        postType: draftData.postType || 'POST',
        price: draftData.price || '',
        targetAmount: draftData.targetAmount || '',
        pollQuestion: draftData.pollQuestion || '',
        pollOptions: draftData.pollOptions || [],
        allowMultipleChoices: draftData.allowMultipleChoices || false,
        mediaItems: uploadedMedia,
        taggedCreators: draftData.taggedCreators || [],
        categories: draftData.categories || [],
        scheduledFor: draftData.scheduledFor || '',
        savedAt: Date.now()
      };

      // Only save if there's actual content
      const hasContent = draft.content.replace(/<[^>]*>/g, '').trim().length > 0;
      const hasMedia = uploadedMedia.length > 0;
      const hasPollData = draft.postType === 'POLL' && draft.pollQuestion.trim().length > 0;

      console.log('[Draft] Checking save:', { hasContent, hasMedia, hasPollData, content: draft.content });

      if (hasContent || hasMedia || hasPollData) {
        localStorage.setItem(DRAFT_KEY, JSON.stringify(draft));
        setHasDraft(true);
        console.log('[Draft] Saved to localStorage');
        return true;
      }
      return false;
    } catch (error) {
      console.warn('Failed to save draft:', error);
      return false;
    }
  }, [isEditing]);

  // Load draft from localStorage
  const loadDraft = useCallback(() => {
    if (isEditing) return null;

    try {
      const draftJson = localStorage.getItem(DRAFT_KEY);
      if (!draftJson) return null;

      const draft = JSON.parse(draftJson);
      
      // Check if draft is too old (24 hours)
      const maxAge = 24 * 60 * 60 * 1000;
      if (Date.now() - draft.savedAt > maxAge) {
        localStorage.removeItem(DRAFT_KEY);
        setHasDraft(false);
        return null;
      }

      return draft;
    } catch (error) {
      console.warn('Failed to load draft:', error);
      return null;
    }
  }, [isEditing]);

  // Clear draft from localStorage
  const clearDraft = useCallback(() => {
    localStorage.removeItem(DRAFT_KEY);
    setHasDraft(false);
  }, []);

  return {
    hasDraft,
    saveDraft,
    loadDraft,
    clearDraft
  };
};

export default useDraftPost;
